/**
 * Valou Feedback Service
 *
 * Speichert User-Feedback zu Ort-Nutzungen (Valou-Bereiche).
 * Feedback wird gesammelt, um die automatische Valou-Zuordnung zu verbessern.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  query,
  where,
  serverTimestamp,
  Timestamp,
} from '@firebase/firestore';
import { db } from './firebase';
import type {
  ValouFeedback,
  ValouFeedbackCreateInput,
  ValouFeedbackSource
} from '../types';
import type { ValouAreaId } from '../types/valou';

const COLLECTION_NAME = 'valouFeedback';

/**
 * Speichert Valou-Feedback fuer einen Ort
 */
export async function saveValouFeedback(
  placeId: string,
  userId: string,
  areas: ValouAreaId[],
  source: ValouFeedbackSource
): Promise<string> {
  // Eindeutige ID aus placeId + userId + source
  const feedbackId = `${placeId}_${userId}_${source}`;
  const docRef = doc(db, COLLECTION_NAME, feedbackId);

  const existingDoc = await getDoc(docRef);

  if (existingDoc.exists()) {
    // Update existing feedback
    await setDoc(docRef, {
      areas,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  } else {
    // Create new feedback
    await setDoc(docRef, {
      placeId,
      userId,
      areas,
      source,
      createdAt: serverTimestamp(),
    });
  }

  return feedbackId;
}

/**
 * Prueft, ob User bereits Feedback fuer einen Ort gegeben hat
 */
export async function hasUserFeedbackForPlace(
  placeId: string,
  userId: string,
  source: ValouFeedbackSource = 'favorite'
): Promise<boolean> {
  const feedbackId = `${placeId}_${userId}_${source}`;
  const docRef = doc(db, COLLECTION_NAME, feedbackId);
  const docSnap = await getDoc(docRef);
  return docSnap.exists();
}

/**
 * Holt Feedback eines Users fuer einen Ort
 */
export async function getUserFeedbackForPlace(
  placeId: string,
  userId: string,
  source: ValouFeedbackSource = 'favorite'
): Promise<ValouAreaId[] | null> {
  const feedbackId = `${placeId}_${userId}_${source}`;
  const docRef = doc(db, COLLECTION_NAME, feedbackId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  const data = docSnap.data() as ValouFeedback;
  return data.areas || [];
}

/**
 * Holt aggregiertes Feedback fuer einen Ort (alle User)
 * Gibt zurueck, wie oft jeder Valou-Bereich gewaehlt wurde
 */
export async function getAggregatedFeedbackForPlace(
  placeId: string
): Promise<Record<ValouAreaId, number>> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('placeId', '==', placeId)
  );

  const snapshot = await getDocs(q);

  const counts: Record<string, number> = {
    privatesLeben: 0,
    persoenlichkeitSkills: 0,
    taetigkeit: 0,
    stilWirkung: 0,
    ressourcenMittel: 0,
    gesundheit: 0,
  };

  snapshot.forEach((doc) => {
    const data = doc.data() as ValouFeedback;
    if (data.areas) {
      data.areas.forEach((area) => {
        if (counts[area] !== undefined) {
          counts[area]++;
        }
      });
    }
  });

  return counts as Record<ValouAreaId, number>;
}

/**
 * Holt die Top Valou-Bereiche fuer einen Ort basierend auf User-Feedback
 * Nuetzlich fuer die Anzeige oder zur Verbesserung der automatischen Berechnung
 */
export async function getTopValouAreasFromFeedback(
  placeId: string,
  minVotes: number = 2
): Promise<ValouAreaId[]> {
  const counts = await getAggregatedFeedbackForPlace(placeId);

  return (Object.entries(counts) as [ValouAreaId, number][])
    .filter(([_, count]) => count >= minVotes)
    .sort((a, b) => b[1] - a[1])
    .map(([areaId]) => areaId);
}

/**
 * Zaehlt die Gesamtanzahl der Feedbacks fuer einen Ort
 */
export async function getFeedbackCountForPlace(placeId: string): Promise<number> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('placeId', '==', placeId)
  );

  const snapshot = await getDocs(q);
  return snapshot.size;
}
